import { useState } from 'react';
import { Badge, Button, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { faArrowRotateRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Sentry from 'src/services/sentry';

import OrderStatusBadge from 'src/components/custom_components/OrderStatusBadge';
import WarnNoConnectedCameraModal from 'src/components/custom_components/WarnNoConnectedCameraModal';
import OHResults from 'src/components/OHResults/OHResults';
import { ResumeInformationModal } from 'src/components/ResumeInformationModal';

import { I18NextNS } from 'src/models/Language.model';
import { Session } from 'src/models/Session.model';

import useCheckCameraConnection from 'src/hooks/useCheckCameraConnection';
import useLoadSessions from 'src/hooks/useLoadSessions';

const TeleophthResults = () => {
  const { t } = useTranslation(I18NextNS.PAGES);

  const { isCameraConnected } = useCheckCameraConnection();

  const [isShowResumeModal, setIsShowResumeModal] = useState(false);

  const handleSetIsShowResumeModal = (isShowResumeModal: boolean) => {
    setIsShowResumeModal(isShowResumeModal);
  };

  const [currentSession, setCurrentSession] = useState<Session>();

  const [
    shouldShowWarnNoConnectedCameraModal,
    setShouldShowWarnNoConnectedCameraModal,
  ] = useState(false);

  const {
    currentPage,
    currentSearchValue,
    handleSetCurrentPage,
    handleSetCurrentSearchValue,
    isLoading,
    dataResource,
    handleLoadSessions,
  } = useLoadSessions();

  return (
    <Stack className="h-100">
      <Stack
        direction="horizontal"
        className="justify-content-between align-items-center mb-3"
      >
        <h3 className="mb-0">{t('results.title')}</h3>
      </Stack>
      <div className="text-primary mb-4 d-flex gap-2 align-items-center">
        <FontAwesomeIcon icon={faArrowRotateRight} />
        {t('results.resume')}
      </div>
      <div className="flex-grow-1" style={{ minHeight: 0 }}>
        <OHResults
          currentPage={currentPage}
          currentSearchValue={currentSearchValue}
          handleSetCurrentPage={handleSetCurrentPage}
          handleSetCurrentSearchValue={handleSetCurrentSearchValue}
          isLoading={isLoading}
          dataResource={dataResource}
          handleLoadSessions={handleLoadSessions}
          extraColumns={[
            {
              title: 'Site Name',
              dataIndex: 'siteName',
              render: (record) => {
                return record.siteName;
              },
            },
            {
              title: 'Order Status',
              dataIndex: 'orderStatus',
              render: (record) => {
                if (!record.order) {
                  Sentry.captureMessage(
                    `Missing order in session ${record.iid}`,
                  );
                  return (
                    <Badge pill bg="secondary">
                      N/A
                    </Badge>
                  );
                }

                return OrderStatusBadge(record.order?.Status.value);
              },
            },
            {
              title: 'Actions',
              dataIndex: 'actions',
              render: (record) => {
                return (
                  <Button
                    variant="outline-primary"
                    hidden={!(record.graded && !record.completedReason)}
                    onClick={() => {
                      if (!isCameraConnected) {
                        setShouldShowWarnNoConnectedCameraModal(true);
                        return;
                      }
                      setCurrentSession({
                        ...record,
                        template: record.order?.OrderType.value,
                      });
                      handleSetIsShowResumeModal(true);
                    }}
                    className="rounded-3 px-3 py-1"
                    data-test="resume-exam-button"
                  >
                    <FontAwesomeIcon icon={faArrowRotateRight} />
                  </Button>
                );
              },
            },
          ]}
        />
      </div>
      <div>
        <WarnNoConnectedCameraModal
          isVisible={shouldShowWarnNoConnectedCameraModal}
        />
        {currentSession && (
          <ResumeInformationModal
            currentSession={currentSession}
            isShowResumeModal={isShowResumeModal}
            handleSetIsShowResumeModal={handleSetIsShowResumeModal}
          />
        )}
      </div>
    </Stack>
  );
};

export default TeleophthResults;
